import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import { useNavigate } from "react-router-dom";

import {
  fetchChannelPartnerEarningsSummary,
  fetchChannelPartnerMonthlyEarnings,
} from "../../services/channelPartnerPortalApi";
import ChannelPartnerDashboardLayout from "./ChannelPartnerDashboardLayout";
import {
  COMMUNITY_REFERRAL_EARNING_RUPEES,
  COUNSELLING_COMPLETION_EARNING_RUPEES,
} from "./earningRates";

const formatCurrency = (value) => new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
}).format(Number(value || 0));

const formatMonth = (value) => {
  if (!value || !/^\d{4}-\d{2}$/.test(value)) return value || "--";
  const [year, month] = value.split("-").map(Number);
  return new Intl.DateTimeFormat("en-IN", {
    month: "long",
    year: "numeric",
  }).format(new Date(year, month - 1, 1));
};

const getErrorMessage = (error, fallback) => (
  error?.response?.data?.message || fallback
);

const SummaryCard = ({
  title,
  value,
  hint,
  icon: Icon,
  color,
  background,
  loading,
}) => (
  <Paper
    elevation={0}
    sx={{
      p: 2.25,
      borderRadius: "12px",
      border: "1px solid #EAECF0",
      bgcolor: "#fff",
      minHeight: 142,
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
    }}
  >
    <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2 }}>
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#667085" }}>
        {title}
      </Typography>
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: "10px",
          display: "grid",
          placeItems: "center",
          color,
          bgcolor: background,
        }}
      >
        <Icon sx={{ fontSize: 22 }} />
      </Box>
    </Box>
    <Box>
      <Typography sx={{ fontSize: 27, lineHeight: 1.15, fontWeight: 750, color: "#171725" }}>
        {loading ? "--" : formatCurrency(value)}
      </Typography>
      <Typography sx={{ mt: 0.7, fontSize: 12, color: "#92929D" }}>
        {hint}
      </Typography>
    </Box>
  </Paper>
);

SummaryCard.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  hint: PropTypes.string.isRequired,
  icon: PropTypes.elementType.isRequired,
  color: PropTypes.string.isRequired,
  background: PropTypes.string.isRequired,
  loading: PropTypes.bool.isRequired,
};

SummaryCard.defaultProps = {
  value: 0,
};

const ChannelPartnerEarningsPage = () => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState({});
  const [months, setMonths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const referralRate = Number(
    summary.referral_rate || COMMUNITY_REFERRAL_EARNING_RUPEES,
  );
  const counsellingRate = Number(
    summary.counselling_completion_rate
      || COUNSELLING_COMPLETION_EARNING_RUPEES,
  );

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    Promise.all([
      fetchChannelPartnerEarningsSummary(),
      fetchChannelPartnerMonthlyEarnings(),
    ])
      .then(([summaryResponse, monthlyResponse]) => {
        if (!active) return;
        setSummary(summaryResponse?.data?.data || {});
        setMonths(monthlyResponse?.data?.data?.months || []);
      })
      .catch((requestError) => {
        if (active) {
          setError(
            getErrorMessage(requestError, "Unable to load earnings summary."),
          );
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const openBreakdown = (month) => {
    navigate(
      `/channel-partner-portal/earnings/details?month=${encodeURIComponent(month)}`,
    );
  };

  return (
    <ChannelPartnerDashboardLayout>
      <Box>
        <Box
          sx={{
            mb: 2.2,
            display: "flex",
            alignItems: { xs: "flex-start", md: "center" },
            justifyContent: "space-between",
            flexDirection: { xs: "column", md: "row" },
            gap: 1.5,
          }}
        >
          <Box>
            <Typography sx={{ fontSize: 23, fontWeight: 750, color: "#171725" }}>
              Earnings
            </Typography>
            <Typography sx={{ mt: 0.5, fontSize: 13, color: "#667085" }}>
              Your earnings overview, organized month by month.
            </Typography>
          </Box>
          <Chip
            icon={<CheckCircleOutlineIcon />}
            label={`${formatCurrency(referralRate)} per referred student · ${formatCurrency(counsellingRate)} per completed counselling`}
            sx={{
              height: "auto",
              minHeight: 36,
              py: 0.5,
              bgcolor: "#ECFDF3",
              color: "#027A48",
              fontWeight: 650,
              borderRadius: "8px",
              "& .MuiChip-label": { whiteSpace: "normal" },
              "& .MuiChip-icon": { color: "#039855" },
            }}
          />
        </Box>

        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              xl: "repeat(4, 1fr)",
            },
            gap: 2,
            mb: 2.4,
          }}
        >
          <SummaryCard
            title="Total Earnings"
            value={summary.total_earnings}
            hint="All active earnings"
            icon={AccountBalanceWalletOutlinedIcon}
            color="#3448A4"
            background="#EEF1FF"
            loading={loading}
          />
          <SummaryCard
            title="This Month"
            value={summary.earnings_this_month}
            hint="Earned in the current month"
            icon={CalendarMonthOutlinedIcon}
            color="#B54708"
            background="#FFF4E8"
            loading={loading}
          />
          <SummaryCard
            title="Referral Earnings"
            value={summary.referral_earnings}
            hint={`${formatCurrency(referralRate)} for every referred student`}
            icon={LinkOutlinedIcon}
            color="#027A48"
            background="#ECFDF3"
            loading={loading}
          />
          <SummaryCard
            title="Counselling Earnings"
            value={summary.counselling_earnings}
            hint={`${formatCurrency(counsellingRate)} after completion`}
            icon={CheckCircleOutlineIcon}
            color="#7A5AF8"
            background="#F4F3FF"
            loading={loading}
          />
        </Box>

        <Paper
          elevation={0}
          sx={{
            border: "1px solid #EAECF0",
            borderRadius: "10px",
            bgcolor: "#fff",
            overflow: "hidden",
          }}
        >
          <Box sx={{ px: 2.2, py: 1.8, borderBottom: "1px solid #EAECF0" }}>
            <Typography sx={{ fontSize: 17, fontWeight: 700, color: "#171725" }}>
              Month-wise Breakdown
            </Typography>
            <Typography sx={{ mt: 0.35, fontSize: 12, color: "#667085" }}>
              Open a month to see every student, source, earning and status on a separate page.
            </Typography>
          </Box>
          <TableContainer>
            <Table sx={{ minWidth: 920 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: "#F9FAFB" }}>
                  {[
                    "Month",
                    "Referred Students",
                    "Referral Earnings",
                    "Completed Counselling",
                    "Counselling Earnings",
                    "Total",
                    "",
                  ].map((label) => (
                    <TableCell
                      key={label || "action"}
                      sx={{
                        color: "#667085",
                        fontSize: 12,
                        fontWeight: 650,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                      <CircularProgress size={28} sx={{ color: "#2D315F" }} />
                    </TableCell>
                  </TableRow>
                ) : months.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 6, color: "#667085" }}>
                      No earnings have been recorded yet.
                    </TableCell>
                  </TableRow>
                ) : months.map((month) => (
                  <TableRow
                    key={month.month}
                    hover
                    tabIndex={0}
                    role="button"
                    onClick={() => openBreakdown(month.month)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        openBreakdown(month.month);
                      }
                    }}
                    sx={{
                      cursor: "pointer",
                      "& td": { borderBottom: "1px solid #EAECF0" },
                      "&:hover": { bgcolor: "#FFF8F0 !important" },
                    }}
                  >
                    <TableCell sx={{ fontWeight: 700, color: "#2D315F" }}>
                      {formatMonth(month.month)}
                    </TableCell>
                    <TableCell>{month.referred_students || 0}</TableCell>
                    <TableCell sx={{ color: "#027A48", fontWeight: 650 }}>
                      {formatCurrency(month.referral_earnings)}
                    </TableCell>
                    <TableCell>{month.completed_counselling || 0}</TableCell>
                    <TableCell sx={{ color: "#7A5AF8", fontWeight: 650 }}>
                      {formatCurrency(month.counselling_earnings)}
                    </TableCell>
                    <TableCell sx={{ color: "#171725", fontWeight: 750 }}>
                      {formatCurrency(month.total_earnings)}
                    </TableCell>
                    <TableCell align="right">
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.5,
                          color: "#3568D4",
                        }}
                      >
                        <Typography component="span" sx={{ fontSize: 12, fontWeight: 700 }}>
                          View details
                        </Typography>
                        <ArrowForwardIcon sx={{ fontSize: 17 }} />
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    </ChannelPartnerDashboardLayout>
  );
};

export default ChannelPartnerEarningsPage;
